import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import { useTheme } from '@emotion/react';
import NavigationTabs from '@/components/organisms/RouterNavigationTabs';
import { useAuthCookieHandler } from '@/hooks/useAuthCookieHandler/useAuthCookieHandler';
import { routes } from '@/core/router/routes';
import { useGetBrandQuery, useGetBrandRestaurantsQuery } from '@/services/innovorder/brand/brand.endpoints';
import { useSearchOrdersMutation } from '@/services/innovorder/orders/order.endpoints';
import { UserRoles } from '@/services/innovorder/user/user.type';
import { OrderList } from '@/pages/Order/OrderList/OrderList';

interface ParamTypes {
    brandId: string;
}

export const BrandOrders: React.FunctionComponent<React.PropsWithChildren> = () => {
    const theme = useTheme();
    const { token } = useAuthCookieHandler();
    const { brandId } = useParams<ParamTypes>();
    const brandIdNumber = parseInt(brandId, 10);
    const { data: brand } = useGetBrandQuery({ brandId: brandIdNumber, token }, { refetchOnMountOrArgChange: true });

    const { data: restaurantsData } = useGetBrandRestaurantsQuery(
        { brandId: brandIdNumber },
        { refetchOnMountOrArgChange: true },
    );

    const [fetchOrders, result] = useSearchOrdersMutation();

    const restaurants = useMemo(() => {
        return restaurantsData?.map((restaurant) => ({
            restaurantId: restaurant.restaurantId,
            name: restaurant.name,
            brandId: restaurant.brandId,
        }));
    }, [restaurantsData]);

    const tabRoutes = useMemo(() => routes.filter(({ tab }) => tab === 'brands'), []);

    return (
        <NavigationTabs routes={tabRoutes}>
            <OrderList
                token={token}
                brandId={brandIdNumber}
                restaurants={restaurants}
                userRole={UserRoles.admin}
                fetchOrders={fetchOrders}
                data={result.data}
                isLoading={result.isLoading}
                brandType={brand?.brandType}
                theme={theme}
                headerProps={{
                    title: 'orderList.title',
                    subTitle: 'orderList.subtitle',
                    showRestaurantSelector: true,
                }}
            />
        </NavigationTabs>
    );
};

export default BrandOrders;
